/**
 * /share/report/:id?t=<token> — public, read-only deck viewer. No login: the
 * backend checks the token and that the owner left link sharing on.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Lock, Maximize2, Minimize2 } from 'lucide-react';
import { coworkApi, type PublicPresentation } from '@/cowork/omniad/api/coworkApi';
import { SlideRenderer, deriveTheme } from '@/cowork/omniad/components/slides/SlideRenderer';
import { useVideosMuted } from '@/cowork/omniad/components/slides/videoPrefs';
import { useFullscreen, fullscreenStageStyle } from '@/cowork/omniad/components/slides/useFullscreen';

export function PublicReportPage() {
  const { id = '' } = useParams<{ id: string }>();
  const token = useMemo(() => new URLSearchParams(window.location.search).get('t') || '', []);
  const [state, setState] = useState<{ status: 'loading' } | { status: 'error'; message: string } | { status: 'ok'; d: PublicPresentation }>({ status: 'loading' });
  const [index, setIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const { isFs, toggle: toggleFs, supported: fsSupported } = useFullscreen(stageRef);
  useEffect(() => {
    let cancelled = false;
    if (!id || !token) { setState({ status: 'error', message: 'This link is incomplete.' }); return; }
    coworkApi.presentations.public(id, token)
      .then((d) => { if (!cancelled) setState({ status: 'ok', d }); })
      .catch((e: Error) => { if (!cancelled) setState({ status: 'error', message: e.message }); });
    return () => { cancelled = true; };
  }, [id, token]);
  const d = state.status === 'ok' ? state.d : null;
  const theme = useMemo(() => deriveTheme(d?.brand ?? {}), [d?.brand]);
  const [videosMuted, setVideosMuted] = useVideosMuted();
  const total = d?.slides.length || 0;
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIndex((i) => Math.min(Math.max(total - 1, 0), i + 1)), [total]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); next(); }
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  }, [prev, next]);
  useEffect(() => { if (d) document.title = `${d.title} · Beatly`; }, [d]);

  if (state.status === 'loading') return <div className="flex min-h-screen items-center justify-center bg-[#111318] text-[13px] text-[#98a2b3]"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…</div>;
  if (state.status === 'error') return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f9fc] px-6">
      <div className="max-w-md rounded-2xl border border-[#eaecf0] bg-white p-8 text-center shadow-sm">
        <Lock className="mx-auto h-6 w-6 text-[#98a2b3]" />
        <div className="mt-3 text-[16px] font-semibold text-[#101828]">This report is not available</div>
        <div className="mt-1.5 text-[13px] text-[#667085]">{state.message}</div>
        <div className="mt-4 text-[11.5px] text-[#98a2b3]">Ask whoever sent you the link to turn link sharing back on in Beatly.</div>
      </div>
    </div>
  );
  const deck = d!;
  const brandName = (deck.brand as any)?.brand?.name as string | undefined;
  return (
    <div className="flex min-h-screen flex-col bg-[#111318]">
      <div className="flex items-center gap-3 border-b border-white/10 px-5 py-2.5 text-[12px] text-[#98a2b3]">
        <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white">Shared view</span>
        <span className="min-w-0 flex-1 truncate">{brandName ? `${brandName} · ` : ''}{deck.title}</span>
        <span>{index + 1} / {total}</span>
        <button type="button" onClick={() => setVideosMuted(!videosMuted)} className="rounded-md border border-white/20 px-2 py-1 text-[11.5px] text-white hover:bg-white/10" title={videosMuted ? 'Videos muted — click to unmute' : 'Mute all videos'}>{videosMuted ? 'Muted' : 'Sound on'}</button>
        {fsSupported && <button type="button" onClick={() => void toggleFs()} className="flex items-center gap-1 rounded-md border border-white/20 px-2 py-1 text-[11.5px] font-medium text-white hover:bg-white/10" title="Full screen (Esc to leave)"><Maximize2 className="h-3.5 w-3.5" /> Full screen</button>}
        <a href="https://beatly.com" target="_blank" rel="noreferrer" className="font-semibold text-white hover:underline">Made with Beatly</a>
      </div>
      <div className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <div className="w-full" style={{ maxWidth: 'min(1280px, calc((100vh - 120px) * 16 / 9))' }}>
          {/* Full screen puts this element on screen alone: slide only, its own background, no chrome. */}
          <div ref={stageRef} style={isFs ? fullscreenStageStyle(theme.bg).outer : undefined}>
            <div style={{ position: 'relative', background: theme.bg, borderRadius: isFs ? 0 : 14, overflow: 'hidden', aspectRatio: '16 / 9', boxShadow: isFs ? 'none' : '0 30px 80px rgba(0,0,0,0.55)', containerType: 'size', ...(isFs ? fullscreenStageStyle(theme.bg).inner : {}) } as CSSProperties}>
              {deck.slides[index] && <SlideRenderer slide={deck.slides[index]} index={index} theme={theme} title={deck.title} />}
              {isFs && total > 1 && (
                <>
                  <button type="button" onClick={prev} aria-label="Previous page" style={{ position: 'absolute', left: 0, top: 0, width: '18%', height: '100%', background: 'transparent', border: 0, cursor: index === 0 ? 'default' : 'w-resize' }} />
                  <button type="button" onClick={next} aria-label="Next page" style={{ position: 'absolute', right: 0, top: 0, width: '18%', height: '100%', background: 'transparent', border: 0, cursor: index >= total - 1 ? 'default' : 'e-resize' }} />
                </>
              )}
              {isFs && <button type="button" onClick={() => void toggleFs()} title="Leave full screen (Esc)" style={{ position: 'absolute', right: 12, top: 12, opacity: 0.35, background: 'rgba(0,0,0,0.35)', color: '#fff', border: 0, borderRadius: 8, padding: 6, cursor: 'pointer' }}><Minimize2 className="h-4 w-4" /></button>}
            </div>
          </div>
          {total > 1 && (
            <div className="mt-3 flex items-center justify-center gap-3">
              <button type="button" onClick={prev} disabled={index === 0} className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30" aria-label="Previous page"><ChevronLeft className="h-4 w-4" /></button>
              <div className="flex gap-1.5">{deck.slides.map((_, i) => <button key={i} type="button" onClick={() => setIndex(i)} aria-label={`Page ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === index ? 'w-5 bg-white' : 'w-1.5 bg-white/30 hover:bg-white/60'}`} />)}</div>
              <button type="button" onClick={next} disabled={index >= total - 1} className="rounded-full bg-white/10 p-2 text-white hover:bg-white/20 disabled:opacity-30" aria-label="Next page"><ChevronRight className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
